import React, { useState, useEffect } from "react";
import {
  FaBolt,
  FaPaperPlane,
  FaClock,
  FaRandom,
  FaCodeBranch,
  FaRobot,
  FaGlobe,
  FaTimes,
  FaTrash,
  FaSync,
  FaCommentDots,
  FaComments,
  FaMicrophone,
  FaExchangeAlt,
  FaShopify,
  FaUsers,
  FaFileAlt,
} from "react-icons/fa";
import { apiGet } from "../../utils/api";
import SpintaxHelp from "./SpintaxHelp";

/**
 * Chip-style input for keyword lists. Each chip is one keyword; the matcher
 * applies the chosen mode (contains / exact / startsWith / regex) to each chip
 * independently and fires when ANY one matches.
 */
function KeywordChipsInput({ value = [], onChange, placeholder, hint }) {
  const [draft, setDraft] = useState("");
  const list = Array.isArray(value) ? value : [];

  const addChip = (raw) => {
    const cleaned = String(raw || "").trim().replace(/^["']|["']$/g, "");
    if (!cleaned) return;
    if (list.includes(cleaned)) {
      setDraft("");
      return;
    }
    onChange([...list, cleaned]);
    setDraft("");
  };

  const commitDraft = () => {
    if (draft.trim()) addChip(draft);
  };

  const removeAt = (i) => onChange(list.filter((_, idx) => idx !== i));

  const onKeyDown = (e) => {
    if (e.key === "Enter" || e.key === "," || e.key === "Tab") {
      if (draft.trim()) {
        e.preventDefault();
        addChip(draft);
      }
    } else if (e.key === "Backspace" && !draft && list.length > 0) {
      removeAt(list.length - 1);
    }
  };

  const onPaste = (e) => {
    const text = e.clipboardData?.getData("text") || "";
    if (!text.includes(",")) return;
    e.preventDefault();
    text.split(",").forEach((chunk) => addChip(chunk));
  };

  return (
    <div>
      <div className="input flex flex-wrap gap-1.5 min-h-[40px] items-center cursor-text"
           onClick={(e) => {
             // Focus the inner input when the wrapper is clicked.
             const input = e.currentTarget.querySelector("input");
             if (input) input.focus();
           }}>
        {list.map((kw, i) => (
          <span key={`${kw}-${i}`} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-brand/10 text-brand text-xs font-medium">
            {kw}
            <button
              type="button"
              className="hover:text-red-500"
              onClick={(e) => { e.stopPropagation(); removeAt(i); }}
              aria-label={`Remove ${kw}`}
            >
              <FaTimes size={9} />
            </button>
          </span>
        ))}
        <input
          className="flex-1 min-w-[80px] outline-none border-none bg-transparent text-sm py-0.5"
          placeholder={list.length === 0 ? (placeholder || "Type a keyword and press Enter") : ""}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKeyDown}
          onPaste={onPaste}
          onBlur={commitDraft}
        />
      </div>
      {hint && <p className="input-hint">{hint}</p>}
    </div>
  );
}

/**
 * Read keywords from node data, supporting both the new `keywords: string[]`
 * shape and legacy `keyword: "a, b, c"` strings. Used to migrate old flows
 * the moment a user opens the config panel.
 */
function readKeywords(data) {
  let list = [];
  if (Array.isArray(data?.keywords)) {
    list = data.keywords;
  } else if (typeof data?.keyword === "string" && data.keyword.trim()) {
    list = data.keyword
      .split(",")
      .map((k) => k.trim().replace(/^["']|["']$/g, ""))
      .filter(Boolean);
  }
  // De-duplicate (case-insensitive) so legacy flows that stored the same
  // keyword twice don't show duplicate chips on the canvas.
  const seen = new Set();
  return list.filter((k) => {
    const norm = String(k).trim().toLowerCase();
    if (!norm || seen.has(norm)) return false;
    seen.add(norm);
    return true;
  });
}

const nodeInfo = {
  trigger: { icon: FaBolt, label: "Message Received", color: "bg-brand" },
  sendMessage: { icon: FaPaperPlane, label: "Send Message", color: "bg-blue-500" },
  delay: { icon: FaClock, label: "Delay", color: "bg-amber-500" },
  jitter: { icon: FaRandom, label: "Jitter", color: "bg-fuchsia-500" },
  condition: { icon: FaCodeBranch, label: "Condition", color: "bg-purple-500" },
  triggerWebhook: { icon: FaGlobe, label: "Trigger Webhook", color: "bg-red-500" },
  sendToAI: { icon: FaRobot, label: "Send to AI", color: "bg-indigo-500" },
  waitForReply: { icon: FaCommentDots, label: "Wait for Reply", color: "bg-orange-500" },
  loop: { icon: FaSync, label: "Loop", color: "bg-teal-500" },
  aiConversation: { icon: FaComments, label: "AI Conversation", color: "bg-violet-500" },
  aiAgent: { icon: FaRobot, label: "AI Agent", color: "bg-fuchsia-500" },
  sendVoiceNote: { icon: FaMicrophone, label: "Voice Note", color: "bg-pink-500" },
  httpRequest: { icon: FaExchangeAlt, label: "HTTP Request", color: "bg-cyan-500" },
  shopifyTrigger: { icon: FaShopify, label: "Shopify Event", color: "bg-emerald-600" },
  sendToGroup: { icon: FaUsers, label: "Group Follow-Up", color: "bg-emerald-600" },
  aiAnalyzer: { icon: FaFileAlt, label: "AI Analyzer", color: "bg-sky-600" },
};

const VARIABLES = [
  { key: "{{firstName}}", desc: "First name" },
  { key: "{{lastName}}", desc: "Last name" },
  { key: "{{name}}", desc: "Full name" },
  { key: "{{phone}}", desc: "Phone number" },
  { key: "{{email}}", desc: "Email" },
  { key: "{{companyName}}", desc: "Company name" },
  { key: "{{tags}}", desc: "Tags (comma-separated)" },
  { key: "{{stage}}", desc: "Lead stage" },
];

const SHOPIFY_VARIABLES = [
  { key: "{{shopify.order.name}}", desc: "Order number" },
  { key: "{{shopify.customer.first_name}}", desc: "Customer first name" },
  { key: "{{shopify.customer.last_name}}", desc: "Customer last name" },
  { key: "{{shopify.order.total_price}}", desc: "Order total" },
  { key: "{{shopify.order.currency}}", desc: "Currency" },
  { key: "{{shopify.order.fulfillment_status}}", desc: "Fulfilment status" },
  { key: "{{shopify.order.financial_status}}", desc: "Payment status" },
  { key: "{{shopify.shippingAddress.city}}", desc: "Shipping city" },
];

const NODE_OUTPUT_LABELS = {
  trigger: "Trigger message",
  sendMessage: "Sent message text",
  sendToAI: "AI response",
  aiConversation: "AI conversation",
  aiAgent: "AI agent",
  sendVoiceNote: "Voice note text",
  httpRequest: "HTTP response",
  waitForReply: "User reply",
  condition: "Condition result",
};

function VariableHelper({ onInsert, nodes, currentNodeId, edges }) {
  const [open, setOpen] = useState(false);

  // Find upstream nodes that produce output (connected before this node)
  const upstreamNodes = React.useMemo(() => {
    if (!nodes || !edges || !currentNodeId) return [];
    // Walk edges backwards to find all ancestors
    const ancestors = [];
    const visited = new Set();
    const queue = [currentNodeId];
    while (queue.length > 0) {
      const nid = queue.shift();
      const incoming = edges.filter((e) => e.target === nid);
      for (const edge of incoming) {
        if (!visited.has(edge.source)) {
          visited.add(edge.source);
          const srcNode = nodes.find((n) => n.id === edge.source);
          if (srcNode) ancestors.push(srcNode);
          queue.push(edge.source);
        }
      }
    }
    return ancestors.reverse(); // chronological order
  }, [nodes, edges, currentNodeId]);
  const hasShopifyTrigger = upstreamNodes.some((node) => node.type === "shopifyTrigger");

  return (
    <div className="relative">
      <button
        type="button"
        className="text-xs text-brand hover:underline"
        onClick={() => setOpen(!open)}
      >
        + Insert Variable
      </button>
      {open && (
        <div className="absolute z-10 right-0 mt-1 w-64 bg-surface-card border border-surface-border rounded-lg shadow-lg py-1 max-h-64 overflow-y-auto">
          {/* Node output references */}
          {upstreamNodes.length > 0 && (
            <>
              <div className="px-3 py-1.5 border-b border-surface-border">
                <p className="text-2xs font-semibold text-ink-muted uppercase tracking-wider">Node Outputs</p>
              </div>
              <button
                type="button"
                className="w-full text-left px-3 py-1.5 hover:bg-surface-bg flex items-center justify-between"
                onClick={() => {
                  onInsert("{{prev.output}}");
                  setOpen(false);
                }}
              >
                <code className="text-xs font-mono text-purple-500">{"{{prev.output}}"}</code>
                <span className="text-2xs text-ink-muted">Previous node</span>
              </button>
              {upstreamNodes.map((n) => (
                <button
                  key={n.id}
                  type="button"
                  className="w-full text-left px-3 py-1.5 hover:bg-surface-bg flex items-center justify-between"
                  onClick={() => {
                    onInsert(`{{${n.id}.output}}`);
                    setOpen(false);
                  }}
                >
                  <code className="text-xs font-mono text-purple-500">{`{{${n.id}.output}}`}</code>
                  <span className="text-2xs text-ink-muted truncate ml-1">
                    {NODE_OUTPUT_LABELS[n.type] || n.type}
                  </span>
                </button>
              ))}
              <div className="border-t border-surface-border mt-1" />
            </>
          )}
          {hasShopifyTrigger && (
            <>
              <div className="px-3 py-1.5 border-b border-surface-border">
                <p className="text-2xs font-semibold text-ink-muted uppercase tracking-wider">Shopify order</p>
              </div>
              {SHOPIFY_VARIABLES.map((variable) => (
                <button
                  key={variable.key}
                  type="button"
                  className="w-full text-left px-3 py-1.5 hover:bg-surface-bg flex items-center justify-between gap-2"
                  onClick={() => {
                    onInsert(variable.key);
                    setOpen(false);
                  }}
                >
                  <code className="text-xs font-mono text-emerald-600 truncate">{variable.key}</code>
                  <span className="text-2xs text-ink-muted whitespace-nowrap">{variable.desc}</span>
                </button>
              ))}
            </>
          )}
          {/* Contact variables */}
          <div className="px-3 py-1.5 border-b border-surface-border">
            <p className="text-2xs font-semibold text-ink-muted uppercase tracking-wider">Contact</p>
          </div>
          {VARIABLES.map((v) => (
            <button
              key={v.key}
              type="button"
              className="w-full text-left px-3 py-1.5 hover:bg-surface-bg flex items-center justify-between"
              onClick={() => {
                onInsert(v.key);
                setOpen(false);
              }}
            >
              <code className="text-xs font-mono text-brand">{v.key}</code>
              <span className="text-2xs text-ink-muted">{v.desc}</span>
            </button>
          ))}
          <div className="border-t border-surface-border mt-1 pt-1 px-3 py-1">
            <p className="text-2xs text-ink-faint">
              Custom fields: {"{{custom.fieldName}}"}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

function CloudTemplateControls({ data, templates, onChange }) {
  const templateValue = data.template?.name
    ? `${data.template.name}:${data.template.language || "en_US"}`
    : "";

  return (
    <div className="grid gap-3">
      <div className="input-group">
        <label className="input-label">Cloud Send Mode</label>
        <select
          className="input"
          value={data.sendMode || "auto"}
          onChange={(e) => {
            const sendMode = e.target.value;
            onChange({
              sendMode,
              kind: sendMode === "template" ? "template" : undefined,
              template: sendMode === "template" ? data.template || null : null,
            });
          }}
        >
          <option value="auto">Auto</option>
          <option value="freeform">Free-form inside 24h</option>
          <option value="template">Approved template</option>
        </select>
      </div>
      {(data.sendMode === "template" || data.kind === "template") && (
        <div className="input-group">
          <label className="input-label">Template</label>
          <select
            className="input"
            value={templateValue}
            onChange={(e) => {
              const [name, language] = e.target.value.split(":");
              const template = templates.find((tpl) => tpl.name === name && (tpl.language || "en_US") === language);
              onChange({
                sendMode: "template",
                kind: "template",
                template: template ? { name: template.name, language: template.language || "en_US", components: template.components || [] } : null,
              });
            }}
          >
            <option value="">Choose approved template</option>
            {templates.map((tpl) => (
              <option key={tpl.id || `${tpl.name}:${tpl.language}`} value={`${tpl.name}:${tpl.language || "en_US"}`}>
                {tpl.name} ({tpl.language || "en_US"})
              </option>
            ))}
          </select>
        </div>
      )}
    </div>
  );
}

/**
 * Shared toggle letting an AI node read the attachment(s) the customer sent.
 */
function AttachmentAnalysisToggle({ data, updateData, accent = "indigo" }) {
  const on = data.analyzeAttachments !== false;
  const accentClass = {
    indigo: "accent-indigo-500",
    violet: "accent-violet-500",
    fuchsia: "accent-fuchsia-500",
  }[accent] || "accent-indigo-500";
  return (
    <div className="input-group">
      <label className="flex items-center gap-2 cursor-pointer">
        <input
          type="checkbox"
          className={accentClass}
          checked={on}
          onChange={(e) => updateData("analyzeAttachments", e.target.checked)}
        />
        <span className="text-xs font-medium text-ink-primary">
          Read attachments (PDF, audio, images)
        </span>
      </label>
      <p className="input-hint mt-1">
        When the customer sends a document, voice note, or image, its contents are
        extracted and given to the AI alongside the message.
      </p>
      {on && (
        <textarea
          className="input min-h-[60px] mt-2"
          placeholder="Optional: what to extract, e.g. 'Give me the invoice total and due date.'"
          value={data.attachmentPrompt || ""}
          onChange={(e) => updateData("attachmentPrompt", e.target.value)}
        />
      )}
    </div>
  );
}

/**
 * Pick WhatsApp groups for a session. Lazily loads the group list the first
 * time the picker renders (and whenever the session changes), with search and
 * multi-select. Mirrors the GroupExport page's data source.
 */
function GroupPicker({ sessionId, selectedIds = [], onChange }) {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!sessionId) {
      setGroups([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    apiGet(`/api/groups/${sessionId}`)
      .then((res) => {
        if (cancelled) return;
        const data = Array.isArray(res) ? res : res?.data || res?.groups || [];
        setGroups(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (!cancelled) setGroups([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  const toggle = (group) => {
    const exists = selectedIds.includes(group.id);
    const nextIds = exists
      ? selectedIds.filter((id) => id !== group.id)
      : [...selectedIds, group.id];
    const nextNames = nextIds
      .map((id) => groups.find((g) => g.id === id)?.name)
      .filter(Boolean);
    onChange(nextIds, nextNames);
  };

  const filtered = groups.filter((g) =>
    (g.name || "").toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="input-group">
      <label className="input-label">Groups</label>
      {!sessionId ? (
        <p className="text-xs text-ink-muted">Select a flow session first.</p>
      ) : (
        <>
          <button
            type="button"
            className="input flex items-center justify-between text-left"
            onClick={() => setOpen((o) => !o)}
          >
            <span className={selectedIds.length ? "text-ink-primary text-sm" : "text-ink-muted text-sm"}>
              {selectedIds.length === 0
                ? "Choose groups"
                : `${selectedIds.length} group${selectedIds.length === 1 ? "" : "s"} selected`}
            </span>
            <span className="text-ink-faint text-xs">{open ? "▲" : "▼"}</span>
          </button>

          {open && (
            <div className="mt-1 border border-surface-border rounded-lg bg-surface-card overflow-hidden">
              <div className="p-2 border-b border-surface-border">
                <input
                  className="input text-xs"
                  placeholder="Search groups..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
              <div className="max-h-48 overflow-y-auto">
                {loading ? (
                  <p className="text-xs text-ink-muted p-3">Loading groups…</p>
                ) : filtered.length === 0 ? (
                  <p className="text-xs text-ink-muted p-3">
                    {groups.length === 0 ? "No groups found for this session." : "No matches."}
                  </p>
                ) : (
                  filtered.map((g) => {
                    const checked = selectedIds.includes(g.id);
                    return (
                      <label
                        key={g.id}
                        className={`flex items-center gap-2 px-3 py-2 cursor-pointer transition-colors ${
                          checked ? "bg-emerald-50" : "hover:bg-surface-bg"
                        }`}
                      >
                        <input
                          type="checkbox"
                          className="accent-emerald-500"
                          checked={checked}
                          onChange={() => toggle(g)}
                        />
                        <div className="min-w-0">
                          <p className="text-xs font-medium text-ink-primary truncate">{g.name}</p>
                          <p className="text-2xs text-ink-muted">{g.size} members</p>
                        </div>
                      </label>
                    );
                  })
                )}
              </div>
            </div>
          )}
        </>
      )}
      {Array.isArray(selectedIds) && selectedIds.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-1.5">
          {selectedIds.map((id) => {
            const g = groups.find((x) => x.id === id);
            return (
              <span
                key={id}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-emerald-100 text-emerald-700 text-2xs font-medium"
              >
                {g?.name || id}
                <button
                  type="button"
                  className="hover:text-red-500"
                  onClick={() => toggle({ id })}
                  aria-label={`Remove ${g?.name || id}`}
                >
                  <FaTimes size={8} />
                </button>
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function NodeConfigPanel({ node, onChange, onClose, onDelete, nodes, edges, sessions = [], selectedSessionId = "" }) {
  const [knowledgeBases, setKnowledgeBases] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [shopifyIntegrations, setShopifyIntegrations] = useState([]);

  useEffect(() => {
    if (node?.type === "sendToAI" || node?.type === "aiConversation" || node?.type === "aiAgent") {
      apiGet("/api/settings/ai/knowledge").then((res) => {
        if (res.success) setKnowledgeBases(res.data || []);
      }).catch(() => {});
    }
  }, [node?.type]);

  useEffect(() => {
    if (node?.type !== "shopifyTrigger") return;
    apiGet("/api/integrations/shopify").then((res) => {
      setShopifyIntegrations((res?.data || []).filter((integration) => integration.status === "connected"));
    }).catch(() => setShopifyIntegrations([]));
  }, [node?.type]);

  const selectedSession = sessions.find((s) => s.sessionId === selectedSessionId);
  const isCloudSession = selectedSession?.transportType === "walytic_cloud";

  useEffect(() => {
    if (!isCloudSession || !selectedSessionId) {
      setTemplates([]);
      return;
    }
    apiGet(`/api/cloud/sessions/${selectedSessionId}/templates`).then((res) => {
      setTemplates((res?.templates || []).filter((template) => template.status === "APPROVED"));
    }).catch(() => setTemplates([]));
  }, [isCloudSession, selectedSessionId]);

  if (!node) return null;

  const info = nodeInfo[node.type] || nodeInfo.trigger;
  const Icon = info.icon;
  const data = node.data || {};

  const updateData = (key, value) => {
    onChange({ ...node, data: { ...data, [key]: value } });
  };

  const patchData = (patch) => {
    onChange({ ...node, data: { ...data, ...patch } });
  };

  const insertVariable = (varKey, field = "message") => {
    const current = data[field] || "";
    updateData(field, current + varKey);
  };

  return (
    <div className="w-full md:w-72 border-l border-surface-border bg-surface-card flex flex-col h-full overflow-hidden">
      {/* Mobile drag handle */}
      <div className="flex justify-center py-2 md:hidden">
        <div className="w-10 h-1 rounded-full bg-surface-border" />
      </div>

      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-surface-border">
        <div className="flex items-center gap-2">
          <div className={`w-6 h-6 rounded-md ${info.color} flex items-center justify-center`}>
            <Icon size={10} className="text-white" />
          </div>
          <span className="text-sm font-semibold text-ink-primary">{info.label}</span>
        </div>
        <button onClick={onClose} className="ml-auto text-ink-muted hover:text-ink-primary p-1">
          <FaTimes size={14} />
        </button>
      </div>

      {/* Form */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* TRIGGER CONFIG */}
        {node.type === "trigger" && (
          <>
            <div className="input-group">
              <label className="input-label">Match Type</label>
              <select
                className="input"
                value={data.matchType || "contains"}
                onChange={(e) => updateData("matchType", e.target.value)}
              >
                <option value="contains">Contains</option>
                <option value="exact">Exact Match</option>
                <option value="startsWith">Starts With</option>
                <option value="regex">Regex</option>
                <option value="any">Any message</option>
              </select>
            </div>
            {data.matchType === "any" ? (
              <div className="rounded-lg bg-sky-50 border border-sky-200 p-3">
                <p className="text-xs text-sky-700">
                  Fires on every incoming message, including file-only messages with no caption.
                  Pair with an <strong>AI Analyzer</strong> node to read attachments.
                </p>
              </div>
            ) : (
              <div className="input-group">
                <label className="input-label">Keywords</label>
                <KeywordChipsInput
                  value={readKeywords(data)}
                  onChange={(arr) => updateData("keywords", arr)}
                  placeholder="e.g. hello (press Enter to add)"
                  hint="Trigger fires when ANY of these keywords matches under the selected mode."
                />
              </div>
            )}
          </>
        )}

        {/* SHOPIFY TRIGGER CONFIG */}
        {node.type === "shopifyTrigger" && (
          <>
            <div className="input-group">
              <label className="input-label">Shopify connection</label>
              <select
                className="input"
                value={data.integrationId || ""}
                onChange={(e) => {
                  const integration = shopifyIntegrations.find((item) => item.id === e.target.value);
                  patchData({
                    integrationId: e.target.value,
                    integrationName: integration?.name || integration?.shopDomain || "",
                  });
                }}
              >
                <option value="">Choose a connected store</option>
                {shopifyIntegrations.map((integration) => (
                  <option key={integration.id} value={integration.id}>
                    {integration.name} — {integration.shopDomain}
                  </option>
                ))}
              </select>
              {shopifyIntegrations.length === 0 && (
                <p className="input-hint">
                  Add a store under <a className="text-brand hover:underline" href="/shopify">Shopify Integration</a> first.
                </p>
              )}
            </div>
            <div className="input-group">
              <label className="input-label">Event</label>
              <select
                className="input"
                value={data.topic || "orders/create"}
                onChange={(e) => updateData("topic", e.target.value)}
              >
                <option value="orders/create">Order created</option>
                <option value="orders/paid">Order paid</option>
                <option value="orders/fulfilled">Order fulfilled</option>
                <option value="orders/cancelled">Order cancelled</option>
              </select>
            </div>
            <div className="rounded-lg bg-emerald-50 border border-emerald-100 p-3 text-xs text-emerald-900">
              Shopify verifies the event with its signing secret. Connect this node to a Send Message node and use Shopify variables in the message.
            </div>
          </>
        )}

        {/* SEND MESSAGE CONFIG */}
        {node.type === "sendMessage" && (
          <>
            {isCloudSession && (
              <CloudTemplateControls data={data} templates={templates} onChange={patchData} />
            )}
            <div className="input-group">
              <div className="flex items-center justify-between mb-1">
                <label className="input-label mb-0">Message</label>
                <VariableHelper onInsert={(v) => insertVariable(v, "message")} nodes={nodes} edges={edges} currentNodeId={node.id} />
              </div>
              <textarea
                className="input min-h-[100px]"
                placeholder="Type the reply message... Use {Hi|Hello} for spintax randomisation."
                value={data.message || ""}
                onChange={(e) => updateData("message", e.target.value)}
              />
              <p className="input-hint">
                Variables: {"{{firstName}}"}. Spintax: {"{Hi|Hello|Hey}"} picks one per recipient.
              </p>
              <SpintaxHelp />
            </div>
            <div className="input-group">
              <label className="input-label">Media URL (optional)</label>
              <input
                className="input"
                placeholder="https://example.com/image.jpg"
                value={data.mediaUrl || ""}
                onChange={(e) => updateData("mediaUrl", e.target.value)}
              />
              <p className="input-hint">Attach an image, video, or document.</p>
            </div>
          </>
        )}

        {/* DELAY CONFIG */}
        {node.type === "delay" && (
          <>
            <div className="input-group">
              <label className="input-label">Duration</label>
              <input
                type="number"
                className="input"
                min="1"
                placeholder="5"
                value={data.duration || ""}
                onChange={(e) => updateData("duration", Number(e.target.value))}
              />
            </div>
            <div className="input-group">
              <label className="input-label">Unit</label>
              <select
                className="input"
                value={data.unit || "minutes"}
                onChange={(e) => updateData("unit", e.target.value)}
              >
                <option value="seconds">Seconds</option>
                <option value="minutes">Minutes</option>
                <option value="hours">Hours</option>
                <option value="days">Days</option>
              </select>
            </div>
          </>
        )}

        {/* JITTER CONFIG */}
        {node.type === "jitter" && (
          <>
            <p className="text-xs text-ink-muted mb-3">
              Wait a random duration in the range below. The system picks a new value each time the flow reaches this step. Useful for mimicking human sending cadence.
            </p>
            <div className="input-group" style={{ display: "flex", gap: 8 }}>
              <div style={{ flex: 1 }}>
                <label className="input-label">Min</label>
                <input
                  type="number"
                  className="input"
                  min="1"
                  placeholder="10"
                  value={data.minDuration || ""}
                  onChange={(e) => updateData("minDuration", Number(e.target.value))}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label className="input-label">Max</label>
                <input
                  type="number"
                  className="input"
                  min="1"
                  placeholder="60"
                  value={data.maxDuration || ""}
                  onChange={(e) => updateData("maxDuration", Number(e.target.value))}
                />
              </div>
            </div>
            <div className="input-group">
              <label className="input-label">Unit</label>
              <select
                className="input"
                value={data.unit || "seconds"}
                onChange={(e) => updateData("unit", e.target.value)}
              >
                <option value="seconds">Seconds</option>
                <option value="minutes">Minutes</option>
                <option value="hours">Hours</option>
              </select>
            </div>
            {Number(data.minDuration) > 0 &&
              Number(data.maxDuration) > 0 &&
              Number(data.minDuration) > Number(data.maxDuration) && (
                <p className="text-xs text-red-600 mt-1">
                  Min must be ≤ Max.
                </p>
              )}
          </>
        )}

        {/* CONDITION CONFIG */}
        {node.type === "condition" && (
          <>
            <div className="input-group">
              <label className="input-label">Match Type</label>
              <select
                className="input"
                value={data.matchType || "contains"}
                onChange={(e) => updateData("matchType", e.target.value)}
              >
                <option value="contains">Contains</option>
                <option value="exact">Exact Match</option>
                <option value="startsWith">Starts With</option>
                <option value="regex">Regex</option>
              </select>
            </div>
            <div className="input-group">
              <label className="input-label">Keywords</label>
              <KeywordChipsInput
                value={readKeywords(data)}
                onChange={(arr) => updateData("keywords", arr)}
                placeholder="e.g. yes (press Enter to add)"
                hint='If ANY keyword matches under the selected mode, follow the "Yes" path. Otherwise "No".'
              />
            </div>
          </>
        )}

        {/* TRIGGER WEBHOOK CONFIG */}
        {node.type === "triggerWebhook" && (
          <>
            <div className="input-group">
              <label className="input-label">Webhook URL</label>
              <input
                className="input"
                placeholder="https://your-server.com/webhook"
                value={data.webhookUrl || ""}
                onChange={(e) => updateData("webhookUrl", e.target.value)}
              />
              <p className="input-hint">
                POST request will be sent to this URL when triggered.
              </p>
            </div>
            <div className="input-group">
              <label className="input-label">Custom Headers (optional)</label>
              <textarea
                className="input min-h-[60px] font-mono text-xs"
                placeholder='{"Authorization": "Bearer token"}'
                value={data.customHeaders || ""}
                onChange={(e) => updateData("customHeaders", e.target.value)}
              />
              <p className="input-hint">JSON object of extra headers to send.</p>
            </div>
            <div className="rounded-lg bg-surface-bg p-3 space-y-1.5">
              <p className="text-xs font-semibold text-ink-primary">Webhook payload includes:</p>
              <ul className="text-2xs text-ink-muted space-y-0.5">
                <li>• <code className="font-mono text-brand">contact</code> — sender's WhatsApp JID</li>
                <li>• <code className="font-mono text-brand">contactNumber</code> — phone number</li>
                <li>• <code className="font-mono text-brand">message</code> — incoming message text</li>
                <li>• <code className="font-mono text-brand">timestamp</code> — ISO timestamp</li>
                <li>• <code className="font-mono text-brand">sessionId</code> — WhatsApp session</li>
                <li>• <code className="font-mono text-brand">flowName</code> — flow name</li>
              </ul>
            </div>
          </>
        )}

        {/* SEND TO AI CONFIG */}
        {node.type === "sendToAI" && (
          <>
            <div className="input-group">
              <label className="input-label">System Prompt</label>
              <textarea
                className="input min-h-[80px]"
                placeholder="You are a helpful customer support agent for [Company]. Answer questions based on the knowledge base provided."
                value={data.prompt || ""}
                onChange={(e) => updateData("prompt", e.target.value)}
              />
              <p className="input-hint">
                Instructions for the AI. The incoming message + knowledge base will be sent automatically.
              </p>
            </div>
            <div className="input-group">
              <label className="input-label">Knowledge Bases</label>
              {knowledgeBases.length > 0 ? (
                <div className="space-y-1.5 max-h-40 overflow-y-auto">
                  {knowledgeBases.map((kb) => {
                    const selected = (data.knowledgeBaseIds || []).includes(kb.id);
                    return (
                      <label
                        key={kb.id}
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-colors ${
                          selected
                            ? "border-indigo-400 bg-indigo-50"
                            : "border-surface-border hover:bg-surface-bg"
                        }`}
                      >
                        <input
                          type="checkbox"
                          className="accent-indigo-500"
                          checked={selected}
                          onChange={() => {
                            const ids = data.knowledgeBaseIds || [];
                            const next = selected
                              ? ids.filter((id) => id !== kb.id)
                              : [...ids, kb.id];
                            updateData("knowledgeBaseIds", next);
                          }}
                        />
                        <div>
                          <p className="text-xs font-medium text-ink-primary">{kb.name}</p>
                          <p className="text-2xs text-ink-muted">{kb.type} — {(kb.content || "").length} chars</p>
                        </div>
                      </label>
                    );
                  })}
                </div>
              ) : (
                <p className="text-xs text-ink-muted">
                  No knowledge bases yet. Go to Settings → AI to add one.
                </p>
              )}
              <p className="input-hint mt-1">
                If none selected, all active knowledge bases will be used.
              </p>
            </div>
            <div className="input-group">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  className="accent-indigo-500"
                  checked={data.silentMode || false}
                  onChange={(e) => updateData("silentMode", e.target.checked)}
                />
                <span className="text-xs font-medium text-ink-primary">Silent mode</span>
              </label>
              <p className="input-hint mt-1">
                Don't send AI reply as a message. Only pass it as output to the next node (e.g. Voice Note, HTTP Request).
              </p>
            </div>
            <AttachmentAnalysisToggle data={data} updateData={updateData} accent="indigo" />
            <div className="rounded-lg bg-indigo-50 border border-indigo-200 p-3">
              <p className="text-xs text-indigo-700">
                Requires an OpenAI API key. Set it in <strong>Settings → AI</strong>.
              </p>
            </div>
          </>
        )}

        {/* AI CONVERSATION CONFIG */}
        {node.type === "aiConversation" && (
          <>
            <div className="input-group">
              <label className="input-label">System Prompt</label>
              <textarea
                className="input min-h-[80px]"
                placeholder="You are a helpful customer support agent for [Company]. Answer questions based on the knowledge base provided."
                value={data.prompt || ""}
                onChange={(e) => updateData("prompt", e.target.value)}
              />
              <p className="input-hint">
                Instructions for the AI. The incoming message + knowledge base will be sent automatically.
              </p>
            </div>
            <div className="input-group">
              <label className="input-label">Knowledge Bases</label>
              {knowledgeBases.length > 0 ? (
                <div className="space-y-1.5 max-h-40 overflow-y-auto">
                  {knowledgeBases.map((kb) => {
                    const selected = (data.knowledgeBaseIds || []).includes(kb.id);
                    return (
                      <label
                        key={kb.id}
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-colors ${
                          selected
                            ? "border-violet-400 bg-violet-50"
                            : "border-surface-border hover:bg-surface-bg"
                        }`}
                      >
                        <input
                          type="checkbox"
                          className="accent-violet-500"
                          checked={selected}
                          onChange={() => {
                            const ids = data.knowledgeBaseIds || [];
                            const next = selected
                              ? ids.filter((id) => id !== kb.id)
                              : [...ids, kb.id];
                            updateData("knowledgeBaseIds", next);
                          }}
                        />
                        <div>
                          <p className="text-xs font-medium text-ink-primary">{kb.name}</p>
                          <p className="text-2xs text-ink-muted">{kb.type} — {(kb.content || "").length} chars</p>
                        </div>
                      </label>
                    );
                  })}
                </div>
              ) : (
                <p className="text-xs text-ink-muted">
                  No knowledge bases yet. Go to Settings → AI to add one.
                </p>
              )}
              <p className="input-hint mt-1">
                If none selected, all active knowledge bases will be used.
              </p>
            </div>
            <div className="input-group">
              <label className="input-label">Exit Keyword</label>
              <input
                className="input"
                placeholder="stop"
                value={data.exitKeyword || ""}
                onChange={(e) => updateData("exitKeyword", e.target.value)}
              />
              <p className="input-hint">
                Contact can type this to end the AI conversation.
              </p>
            </div>
            <div className="input-group">
              <label className="input-label">Max Turns</label>
              <input
                type="number"
                className="input"
                min="0"
                placeholder="0"
                value={data.maxTurns != null ? data.maxTurns : 0}
                onChange={(e) => updateData("maxTurns", Number(e.target.value))}
              />
              <p className="input-hint">
                0 = unlimited conversation.
              </p>
            </div>
            <AttachmentAnalysisToggle data={data} updateData={updateData} accent="violet" />
            <div className="rounded-lg bg-violet-50 border border-violet-200 p-3 space-y-2">
              <p className="text-xs text-violet-700">
                This node enters a persistent AI conversation. Every message from the contact will be answered by AI until they type the exit keyword or reach max turns.
              </p>
            </div>
            <div className="rounded-lg bg-indigo-50 border border-indigo-200 p-3">
              <p className="text-xs text-indigo-700">
                Requires an OpenAI API key. Set it in <strong>Settings → AI</strong>.
              </p>
            </div>
          </>
        )}

        {/* AI AGENT CONFIG */}
        {node.type === "aiAgent" && (
          <>
            <div className="rounded-lg bg-fuchsia-50 border border-fuchsia-200 p-3">
              <p className="text-xs text-fuchsia-800 font-medium">Fully AI-led conversation</p>
              <p className="text-xs text-fuchsia-700 mt-1">
                Train the agent once, then it answers every message on its own — and hands the chat to a
                human the moment it is unsure or the customer asks for one.
              </p>
            </div>

            <div className="input-group">
              <label className="input-label">System Prompt</label>
              <textarea
                className="input min-h-[90px]"
                placeholder="You are a helpful sales assistant for [Company]. Answer questions about our products and pricing."
                value={data.prompt || ""}
                onChange={(e) => updateData("prompt", e.target.value)}
              />
              <p className="input-hint">Who the agent is and how it should talk. Sent as the system instruction.</p>
            </div>

            <div className="input-group">
              <label className="input-label">Objective <span className="text-ink-muted font-normal">(optional)</span></label>
              <input
                className="input"
                placeholder="e.g. Answer product questions and qualify the lead"
                value={data.objective || ""}
                onChange={(e) => updateData("objective", e.target.value)}
              />
              <p className="input-hint">What a good conversation should achieve. Shapes every reply.</p>
            </div>

            <div className="input-group">
              <label className="input-label">Knowledge Bases (training)</label>
              {knowledgeBases.length > 0 ? (
                <div className="space-y-1.5 max-h-40 overflow-y-auto">
                  {knowledgeBases.map((kb) => {
                    const selected = (data.knowledgeBaseIds || []).includes(kb.id);
                    return (
                      <label
                        key={kb.id}
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-colors ${
                          selected
                            ? "border-fuchsia-400 bg-fuchsia-50"
                            : "border-surface-border hover:bg-surface-bg"
                        }`}
                      >
                        <input
                          type="checkbox"
                          className="accent-fuchsia-500"
                          checked={selected}
                          onChange={() => {
                            const ids = data.knowledgeBaseIds || [];
                            const next = selected
                              ? ids.filter((id) => id !== kb.id)
                              : [...ids, kb.id];
                            updateData("knowledgeBaseIds", next);
                          }}
                        />
                        <div>
                          <p className="text-xs font-medium text-ink-primary">{kb.name}</p>
                          <p className="text-2xs text-ink-muted">{kb.type} — {(kb.content || "").length} chars</p>
                        </div>
                      </label>
                    );
                  })}
                </div>
              ) : (
                <p className="text-xs text-ink-muted">
                  No knowledge bases yet. Go to Settings → AI to add your products, FAQs and pricing.
                </p>
              )}
              <p className="input-hint">
                What the agent is allowed to know. If none selected, all active knowledge bases are used.
              </p>
            </div>

            <div className="input-group">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  className="w-4 h-4 rounded accent-fuchsia-500"
                  checked={data.replyToFirstMessage !== false}
                  onChange={(e) => updateData("replyToFirstMessage", e.target.checked)}
                />
                <span className="input-label mb-0">Reply to the message that starts the chat</span>
              </label>
              <p className="input-hint">
                Recommended. The agent answers the first message immediately instead of waiting for a second one.
                Turn off if another node already sends the greeting.
              </p>
            </div>

            <div className="input-group">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  className="w-4 h-4 rounded accent-fuchsia-500"
                  checked={data.pauseOnHumanReply !== false}
                  onChange={(e) => updateData("pauseOnHumanReply", e.target.checked)}
                />
                <span className="input-label mb-0">Pause when a human replies</span>
              </label>
              <p className="input-hint">
                The moment a teammate sends a message in the chat, the agent goes silent and hands over.
              </p>
            </div>

            <div className="input-group">
              <label className="input-label">Handoff Keywords</label>
              <KeywordChipsInput
                value={Array.isArray(data.handoffKeywords) ? data.handoffKeywords : []}
                onChange={(arr) => updateData("handoffKeywords", arr)}
                placeholder="e.g. human (press Enter to add)"
                hint="If the customer's message contains any of these, the agent hands off to a human."
              />
            </div>

            <div className="input-group">
              <label className="input-label">Handoff Message</label>
              <textarea
                className="input min-h-[60px]"
                placeholder="Connecting you to a team member who can help."
                value={data.handoffMessage || ""}
                onChange={(e) => updateData("handoffMessage", e.target.value)}
              />
              <p className="input-hint">Sent to the customer when the agent hands the chat to a human.</p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="input-group">
                <label className="input-label">Exit Keyword</label>
                <input
                  className="input"
                  placeholder="stop"
                  value={data.exitKeyword || ""}
                  onChange={(e) => updateData("exitKeyword", e.target.value)}
                />
                <p className="input-hint">Customer types this to end the AI chat.</p>
              </div>
              <div className="input-group">
                <label className="input-label">Max Turns</label>
                <input
                  className="input"
                  type="number"
                  min="0"
                  placeholder="0"
                  value={data.maxTurns != null ? data.maxTurns : 0}
                  onChange={(e) => updateData("maxTurns", Number(e.target.value))}
                />
                <p className="input-hint">0 = unlimited.</p>
              </div>
            </div>

            <AttachmentAnalysisToggle data={data} updateData={updateData} accent="fuchsia" />

            <div className="rounded-lg bg-indigo-50 border border-indigo-200 p-3">
              <p className="text-xs text-indigo-700">
                Requires an OpenAI API key. Set it in <strong>Settings → AI</strong>.
              </p>
            </div>
          </>
        )}

        {/* WAIT FOR REPLY CONFIG */}
        {node.type === "waitForReply" && (
          <div className="space-y-3">
            <div className="rounded-lg bg-orange-50 border border-orange-200 p-3">
              <p className="text-xs text-orange-700">
                Pauses the flow until the contact replies. Two output branches:
                <br />• <strong>Replied</strong> — fires immediately when the user sends a message
                <br />• <strong>Timeout</strong> — fires if no reply arrives within the window below
              </p>
            </div>

            <div className="input-group">
              <label className="input-label">Timeout</label>
              <div className="flex gap-2">
                <input
                  type="number"
                  min="1"
                  className="input flex-1"
                  placeholder="24"
                  value={
                    data.timeoutValue !== undefined && data.timeoutValue !== ""
                      ? data.timeoutValue
                      : (data.timeoutHours ?? 24)
                  }
                  onChange={(e) => updateData("timeoutValue", e.target.value === "" ? "" : Number(e.target.value))}
                />
                <select
                  className="input flex-1"
                  value={data.timeoutUnit || "hours"}
                  onChange={(e) => updateData("timeoutUnit", e.target.value)}
                >
                  <option value="minutes">Minutes</option>
                  <option value="hours">Hours</option>
                  <option value="days">Days</option>
                </select>
              </div>
              <p className="text-[11px] text-ink-muted mt-1">
                If the contact doesn't reply within this window, the flow continues down the <strong>Timeout</strong> branch.
              </p>
            </div>

            <p className="text-xs text-ink-muted">
              Tip: Connect the <strong>Replied</strong> handle to your reply-handling logic, and the <strong>Timeout</strong> handle to a follow-up message (e.g., "Just checking in...").
            </p>
          </div>
        )}

        {/* LOOP CONFIG */}
        {node.type === "loop" && (
          <>
            {isCloudSession && (
              <CloudTemplateControls data={data} templates={templates} onChange={patchData} />
            )}
            <div className="input-group">
              <div className="flex items-center justify-between mb-1">
                <label className="input-label mb-0">Message *</label>
                <VariableHelper onInsert={(v) => insertVariable(v, "message")} nodes={nodes} edges={edges} currentNodeId={node.id} />
              </div>
              <textarea
                className="input"
                rows={3}
                placeholder="Enter your recurring message..."
                value={data.message || ""}
                onChange={(e) => updateData("message", e.target.value)}
              />
            </div>
            <div className="input-group">
              <label className="input-label">Media URL</label>
              <input
                className="input"
                placeholder="https://example.com/image.jpg (optional)"
                value={data.mediaUrl || ""}
                onChange={(e) => updateData("mediaUrl", e.target.value)}
              />
            </div>
            <div className="input-group">
              <label className="input-label">Recipients</label>
              <textarea
                className="input text-xs"
                rows={3}
                placeholder={"Paste numbers (one per line or comma-separated)\nLeave empty to loop for triggering contact"}
                value={(data.recipients || []).join("\n")}
                onChange={(e) => {
                  const nums = e.target.value
                    .split(/[\n,]+/)
                    .map((n) => n.replace(/\D/g, ""))
                    .filter((n) => n.length >= 7 && n.length <= 15);
                  updateData("recipients", nums);
                }}
              />
              <label className="mt-1 block">
                <input
                  type="file"
                  accept=".csv,.txt"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files && e.target.files[0];
                    if (!file) return;
                    const reader = new FileReader();
                    reader.onload = (ev) => {
                      const lines = ev.target.result.split("\n");
                      const nums = lines
                        .map((l) => l.split(",")[0].replace(/\D/g, ""))
                        .filter((n) => n.length >= 7 && n.length <= 15);
                      const existing = data.recipients || [];
                      const merged = Array.from(new Set([...existing, ...nums])).slice(0, 1000);
                      updateData("recipients", merged);
                    };
                    reader.readAsText(file);
                    e.target.value = "";
                  }}
                />
                <span className="text-xs text-brand cursor-pointer hover:underline">
                  Upload CSV
                </span>
              </label>
              <p className="input-hint">{(data.recipients || []).length}/1000 recipients</p>
            </div>
            <div className="input-group">
              <label className="input-label">Repeat Count</label>
              <input
                type="number"
                className="input"
                min="0"
                placeholder="0 = unlimited"
                value={data.repeatCount != null ? data.repeatCount : 0}
                onChange={(e) => updateData("repeatCount", Number(e.target.value))}
              />
              <p className="input-hint">0 = unlimited (max 10,000 iterations)</p>
            </div>
            <div className="input-group">
              <label className="input-label">Interval Type</label>
              <div className="flex gap-2 mb-2">
                <button
                  type="button"
                  className={"px-3 py-1.5 rounded-lg text-xs font-medium border " +
                    ((data.intervalType || "simple") === "simple"
                      ? "bg-teal-500 text-white border-teal-500"
                      : "border-surface-border text-ink-secondary")}
                  onClick={() => updateData("intervalType", "simple")}
                >
                  Simple
                </button>
                <button
                  type="button"
                  className={"px-3 py-1.5 rounded-lg text-xs font-medium border " +
                    (data.intervalType === "cron"
                      ? "bg-teal-500 text-white border-teal-500"
                      : "border-surface-border text-ink-secondary")}
                  onClick={() => updateData("intervalType", "cron")}
                >
                  Cron
                </button>
              </div>
              {(data.intervalType || "simple") === "simple" ? (
                <div className="flex gap-2">
                  <input
                    type="number"
                    className="input flex-1"
                    min="1"
                    placeholder="24"
                    value={data.intervalValue || ""}
                    onChange={(e) => updateData("intervalValue", Number(e.target.value))}
                  />
                  <select
                    className="input flex-1"
                    value={data.intervalUnit || "hours"}
                    onChange={(e) => updateData("intervalUnit", e.target.value)}
                  >
                    <option value="seconds">Seconds</option>
                    <option value="minutes">Minutes</option>
                    <option value="hours">Hours</option>
                    <option value="days">Days</option>
                  </select>
                </div>
              ) : (
                <div>
                  <input
                    className="input"
                    placeholder="0 10 * * *"
                    value={data.cronExpression || ""}
                    onChange={(e) => updateData("cronExpression", e.target.value)}
                  />
                  <p className="input-hint mt-1">
                    Examples: 0 10 * * * = daily at 10 AM, 0 9 * * 1 = Monday 9 AM
                  </p>
                </div>
              )}
            </div>
          </>
        )}

        {/* SEND VOICE NOTE CONFIG */}
        {node.type === "sendVoiceNote" && (
          <>
            <div className="input-group">
              <div className="flex items-center justify-between mb-1">
                <label className="input-label mb-0">Text to Speak</label>
                <VariableHelper onInsert={(v) => insertVariable(v, "message")} nodes={nodes} edges={edges} currentNodeId={node.id} />
              </div>
              <textarea
                className="input min-h-[80px]"
                placeholder="Type the text that will be converted to a voice note..."
                value={data.message || ""}
                onChange={(e) => updateData("message", e.target.value)}
              />
              <p className="input-hint">
                This text will be converted to speech and sent as a WhatsApp voice note.
              </p>
            </div>
            <div className="input-group">
              <label className="input-label">TTS Provider</label>
              <select
                className="input"
                value={data.ttsProvider || "elevenlabs"}
                onChange={(e) => updateData("ttsProvider", e.target.value)}
              >
                <option value="elevenlabs">ElevenLabs</option>
                <option value="openai">OpenAI TTS</option>
              </select>
            </div>
            {(data.ttsProvider || "elevenlabs") === "elevenlabs" ? (
              <div className="input-group">
                <label className="input-label">Voice ID</label>
                <input
                  className="input font-mono text-xs"
                  placeholder="21m00Tcm4TlvDq8ikWAM (Rachel)"
                  value={data.voiceId || ""}
                  onChange={(e) => updateData("voiceId", e.target.value)}
                />
                <p className="input-hint">
                  Leave empty for default (Rachel). Find voice IDs at{" "}
                  <a href="https://elevenlabs.io/voice-library" target="_blank" rel="noreferrer" className="text-brand hover:underline">
                    ElevenLabs Voice Library
                  </a>.
                </p>
              </div>
            ) : (
              <div className="input-group">
                <label className="input-label">Voice</label>
                <select
                  className="input"
                  value={data.voice || "alloy"}
                  onChange={(e) => updateData("voice", e.target.value)}
                >
                  <option value="alloy">Alloy</option>
                  <option value="echo">Echo</option>
                  <option value="fable">Fable</option>
                  <option value="onyx">Onyx</option>
                  <option value="nova">Nova</option>
                  <option value="shimmer">Shimmer</option>
                </select>
              </div>
            )}
            <div className="rounded-lg bg-pink-50 border border-pink-200 p-3 space-y-1">
              <p className="text-xs text-pink-700">
                Requires an API key configured in <strong>Settings → AI</strong>.
              </p>
              <p className="text-2xs text-pink-600">
                ElevenLabs: ~$0.30/1K chars · OpenAI: ~$0.015/1K chars
              </p>
            </div>
          </>
        )}

        {/* HTTP REQUEST CONFIG */}
        {node.type === "httpRequest" && (
          <>
            <div className="input-group">
              <label className="input-label">Method</label>
              <select
                className="input"
                value={data.method || "GET"}
                onChange={(e) => updateData("method", e.target.value)}
              >
                <option value="GET">GET</option>
                <option value="POST">POST</option>
                <option value="PUT">PUT</option>
                <option value="PATCH">PATCH</option>
                <option value="DELETE">DELETE</option>
              </select>
            </div>
            <div className="input-group">
              <div className="flex items-center justify-between mb-1">
                <label className="input-label mb-0">URL</label>
                <VariableHelper onInsert={(v) => insertVariable(v, "url")} nodes={nodes} edges={edges} currentNodeId={node.id} />
              </div>
              <input
                className="input"
                placeholder="https://api.example.com/endpoint"
                value={data.url || ""}
                onChange={(e) => updateData("url", e.target.value)}
              />
              <p className="input-hint">
                Supports {"{{variables}}"} like {"{{phone}}"} or {"{{name}}"}.
              </p>
            </div>
            <div className="input-group">
              <label className="input-label">Headers (optional)</label>
              <textarea
                className="input min-h-[60px] font-mono text-xs"
                placeholder='{"Authorization": "Bearer token"}'
                value={data.headers || ""}
                onChange={(e) => updateData("headers", e.target.value)}
              />
              <p className="input-hint">JSON object of request headers.</p>
            </div>
            {["POST", "PUT", "PATCH"].includes(data.method || "GET") && (
              <div className="input-group">
                <div className="flex items-center justify-between mb-1">
                  <label className="input-label mb-0">Request Body</label>
                  <VariableHelper onInsert={(v) => insertVariable(v, "body")} nodes={nodes} edges={edges} currentNodeId={node.id} />
                </div>
                <textarea
                  className="input min-h-[80px] font-mono text-xs"
                  placeholder={'{\n  "phone": "{{phone}}",\n  "name": "{{name}}"\n}'}
                  value={data.body || ""}
                  onChange={(e) => updateData("body", e.target.value)}
                />
                <p className="input-hint">
                  JSON body with {"{{variable}}"} support.
                </p>
              </div>
            )}
            <div className="rounded-lg bg-cyan-50 border border-cyan-200 p-3 space-y-1.5">
              <p className="text-xs font-semibold text-cyan-700">Security</p>
              <ul className="text-2xs text-cyan-600 space-y-0.5">
                <li>• Internal/private IPs are blocked (SSRF protection)</li>
                <li>• Walytic admin API endpoints are blocked</li>
                <li>• 15 second timeout per request</li>
              </ul>
            </div>
          </>
        )}

        {/* AI ANALYZER CONFIG */}
        {node.type === "aiAnalyzer" && (
          <>
            <div className="rounded-lg bg-sky-50 border border-sky-200 p-3">
              <p className="text-xs text-sky-800 font-medium">Read what the customer sends</p>
              <p className="text-xs text-sky-700 mt-1">
                Turn a PDF, document, voice note, or image into text, then let the AI answer
                or extract data from it. Pair this with an <strong>Any message</strong> trigger
                so it also fires on media that has no caption.
              </p>
            </div>

            <div className="input-group">
              <label className="input-label">Analysis instruction</label>
              <textarea
                className="input min-h-[80px]"
                placeholder="e.g. Read this invoice and extract the total, due date, and vendor name."
                value={data.instruction || ""}
                onChange={(e) => updateData("instruction", e.target.value)}
              />
              <p className="input-hint">What the AI should look for or produce from the attachment.</p>
            </div>

            <div className="input-group">
              <label className="input-label">Attachment types</label>
              <div className="space-y-1.5">
                {[
                  { key: "any", label: "Any attachment" },
                  { key: "pdf", label: "PDF & documents" },
                  { key: "audio", label: "Audio & voice notes" },
                  { key: "image", label: "Images" },
                ].map((opt) => {
                  const selected = (data.analysisKinds || ["any"]).includes(opt.key);
                  return (
                    <label
                      key={opt.key}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-colors ${
                        selected ? "border-sky-400 bg-sky-50" : "border-surface-border hover:bg-surface-bg"
                      }`}
                    >
                      <input
                        type="checkbox"
                        className="accent-sky-500"
                        checked={selected}
                        onChange={() => {
                          const current = data.analysisKinds || ["any"];
                          let next;
                          if (opt.key === "any") {
                            next = ["any"];
                          } else {
                            const withoutAny = current.filter((k) => k !== "any");
                            next = withoutAny.includes(opt.key)
                              ? withoutAny.filter((k) => k !== opt.key)
                              : [...withoutAny, opt.key];
                            if (next.length === 0) next = ["any"];
                          }
                          updateData("analysisKinds", next);
                        }}
                      />
                      <span className="text-xs font-medium text-ink-primary">{opt.label}</span>
                    </label>
                  );
                })}
              </div>
            </div>

            <div className="input-group">
              <label className="input-label">Knowledge Bases <span className="text-ink-muted font-normal">(optional)</span></label>
              {knowledgeBases.length > 0 ? (
                <div className="space-y-1.5 max-h-40 overflow-y-auto">
                  {knowledgeBases.map((kb) => {
                    const selected = (data.knowledgeBaseIds || []).includes(kb.id);
                    return (
                      <label
                        key={kb.id}
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-colors ${
                          selected ? "border-sky-400 bg-sky-50" : "border-surface-border hover:bg-surface-bg"
                        }`}
                      >
                        <input
                          type="checkbox"
                          className="accent-sky-500"
                          checked={selected}
                          onChange={() => {
                            const ids = data.knowledgeBaseIds || [];
                            const next = selected ? ids.filter((id) => id !== kb.id) : [...ids, kb.id];
                            updateData("knowledgeBaseIds", next);
                          }}
                        />
                        <div>
                          <p className="text-xs font-medium text-ink-primary">{kb.name}</p>
                          <p className="text-2xs text-ink-muted">{kb.type} — {(kb.content || "").length} chars</p>
                        </div>
                      </label>
                    );
                  })}
                </div>
              ) : (
                <p className="text-xs text-ink-muted">
                  No knowledge bases yet. Go to Settings → AI to add one.
                </p>
              )}
            </div>

            <div className="input-group">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  className="accent-sky-500"
                  checked={data.respondInChat !== false}
                  onChange={(e) => updateData("respondInChat", e.target.checked)}
                />
                <span className="text-xs font-medium text-ink-primary">Reply in the chat with the analysis</span>
              </label>
              <p className="input-hint mt-1">
                Off = silent. The analysis is stored as output for the next node via{" "}
                <code className="text-2xs">{`{{prev.output}}`}</code>.
              </p>
            </div>

            <div className="rounded-lg bg-indigo-50 border border-indigo-200 p-3">
              <p className="text-xs text-indigo-700">
                Requires an OpenAI / OpenRouter key in <strong>Settings → AI</strong>. Scanned
                PDFs without a text layer can't be read.
              </p>
            </div>
          </>
        )}

        {/* GROUP FOLLOW-UP CONFIG */}
        {node.type === "sendToGroup" && (
          <>
            <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3">
              <p className="text-xs text-emerald-800 font-medium">Post into WhatsApp groups</p>
              <p className="text-xs text-emerald-700 mt-1">
                Pick one or more groups this number has joined. The message is posted the
                moment the flow reaches this node.
              </p>
            </div>

            <GroupPicker
              sessionId={selectedSessionId}
              selectedIds={data.groupIds || []}
              onChange={(ids, names) => patchData({ groupIds: ids, groupNames: names })}
            />

            <div className="input-group">
              <div className="flex items-center justify-between mb-1">
                <label className="input-label mb-0">Message</label>
                <VariableHelper onInsert={(v) => insertVariable(v, "message")} nodes={nodes} edges={edges} currentNodeId={node.id} />
              </div>
              <textarea
                className="input min-h-[100px]"
                placeholder="Type the message to post in the selected group(s)..."
                value={data.message || ""}
                onChange={(e) => updateData("message", e.target.value)}
              />
              <p className="input-hint">Variables and spintax {"{Hi|Hello}"} work here too.</p>
            </div>

            <div className="input-group">
              <label className="input-label">Media URL (optional)</label>
              <input
                className="input"
                placeholder="https://example.com/image.jpg"
                value={data.mediaUrl || ""}
                onChange={(e) => updateData("mediaUrl", e.target.value)}
              />
              <p className="input-hint">Attach an image, video, or document.</p>
            </div>

            <p className="text-xs text-ink-muted">
              For recurring posts on a schedule, use <strong>Group Follow-Ups</strong> on the
              Flow Builder page instead.
            </p>
          </>
        )}
      </div>

      {/* Footer */}
      <div className="p-4 border-t border-surface-border pb-6 md:pb-4">
        <button
          className="btn-danger btn-sm w-full flex items-center justify-center gap-1.5"
          onClick={() => onDelete(node.id)}
        >
          <FaTrash size={10} />
          Delete Node
        </button>
      </div>
    </div>
  );
}
